import {Box, Button, IconButton, Stack, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useNavigate, useParams} from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {DataGrid, GridColDef, GridRowSelectionModel} from '@mui/x-data-grid';
import {useAccessGroups} from 'src/hooks';
// import {Tenant, Tenants} from 'src/apis/autoGenerate';
// import {
//   accessGroupServiceAddTenant,
//   accessGroupServiceDeleteTenant,
//   accessGroupServiceFindTenant,
// } from 'src/apis';
import {
  accessGroupServiceAddTenant,
  accessGroupServiceDeleteTenant,
  accessGroupServiceFindTenant,
} from 'src/apis/autoGenerate/services';
import {Tenant, Tenants} from 'src/apis/autoGenerate/types';
import Spin from 'src/components/Spin';
import AddTenantToAccessGroupModal from 'src/components/Modals/AddTenantToAccessGroup';
import {formatDate} from 'src/utils/common';
import message from 'src/components/Message';
import SearchInput from 'src/components/SearchInput';

export default function ManageAccessGroupTenant() {
  const {groupId} = useParams();
  const navigate = useNavigate();
  const {getAccessGroups, accessGroups, loading} = useAccessGroups();
  const [groupTenants, setGroupTenants] = useState<Tenant[]>([]);
  const [addTenantModalOpen, setAddTenantModalOpen] = useState(false);
  const [addTenantLoading, setAddTenantLoading] = useState(false);
  const [tenantListLoading, setTenantListLoading] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);

  const targetGroup = useMemo(() => {
    return accessGroups[0];
  }, [accessGroups]);

  const getAccessGroupTenants = useCallback(
    (name?: string) => {
      if (targetGroup) {
        setTenantListLoading(true);
        accessGroupServiceFindTenant(targetGroup.id, name)
          .then(res => {
            const tenants = res.data.tenants;
            if (Array.isArray(tenants)) {
              setGroupTenants(
                tenants.map(_ => ({
                  ..._,
                  firstName: _.user?.firstName,
                  lastName: _.user?.lastName,
                  email: _.user?.email,
                  floorName: _.room?.floorName,
                }))
              );
            } else {
              setGroupTenants([]);
            }
          })
          .finally(() => {
            setTenantListLoading(false);
          });
      }
    },
    [targetGroup]
  );

  useEffect(() => {
    if (groupId) {
      getAccessGroups([groupId]);
    }
  }, [getAccessGroups, groupId]);

  useEffect(() => {
    getAccessGroupTenants();
  }, [getAccessGroupTenants]);

  const handleAddTenants = (tenantIds: string[]) => {
    setAddTenantModalOpen(false);
    setAddTenantLoading(true);
    accessGroupServiceAddTenant({accessGroupId: targetGroup.id, tenantId: tenantIds})
      .then(res => {
        message.success('Add Success');
        getAccessGroupTenants();
      })
      .finally(() => {
        setAddTenantLoading(false);
      });
  };

  const handleRemoveTenants = () => {
    setAddTenantLoading(true);
    accessGroupServiceDeleteTenant({
      accessGroupId: targetGroup.id,
      tenantId: selectedRowIds as string[],
    })
      .then(res => {
        message.success('Remove Success');
        getAccessGroupTenants();
      })
      .finally(() => {
        setAddTenantLoading(false);
      });
  };

  const handleSearchTenantByInfo = (name: string) => {
    getAccessGroupTenants(name);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'user',
        headerName: 'Name',
        width: 250,
        renderCell: val => {
          return (
            <Stack>
              <Typography variant="body2" sx={{fontSize: 14, fontWeight: 500}}>{`${
                val.value?.firstName || ''
              } ${val.value?.lastName || ''}`}</Typography>
              <Typography variant="body2" sx={{color: theme => theme.palette.text.secondary}}>
                {val.value?.email || ''}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'room',
        headerName: 'Unit Name',
        width: 250,
        renderCell: val => {
          return <div>{val.value?.name || ''}</div>;
        },
      },
      {
        field: 'floorName',
        headerName: 'Floor No',
        width: 250,
        renderCell: val => {
          return <div>{val.value || ''}</div>;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Create On',
        width: 250,
        renderCell: val => {
          return <div>{formatDate(val.value)}</div>;
        },
      },
    ],
    []
  );

  return (
    <Box>
      {!targetGroup ? (
        <Spin spinning />
      ) : (
        <>
          <Spin spinning={addTenantLoading} />
          <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 2}}>
            <Stack sx={{display: 'flex', alignItems: 'center'}} spacing={2} direction="row">
              <IconButton
                onClick={() => {
                  navigate(-1);
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Residents in "{targetGroup.name}"</Typography>
            </Stack>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{borderRadius: 16, height: 40, mr: 2}}
                onClick={() => {
                  setAddTenantModalOpen(true);
                }}
              >
                Resident
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                disabled={groupTenants.length < 1 || selectedRowIds.length < 1}
                sx={{
                  borderRadius: 16,
                  height: 40,
                  color: '#324054',
                  borderColor: '#324054',
                  '&:hover': {color: '#0f1317', borderColor: '#0f1317'},
                }}
                onClick={handleRemoveTenants}
              >
                Remove
              </Button>
            </Box>
          </Box>
          <Box>
            <SearchInput
              disabled={groupTenants.length < 1}
              placeholder="Search Tenant"
              onSearch={handleSearchTenantByInfo}
            />
          </Box>
          <Box>
            <DataGrid
              loading={tenantListLoading}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rows={groupTenants}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {page: 0, pageSize: 10},
                },
              }}
              onRowSelectionModelChange={res => {
                setSelectedRowIds(res);
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box textAlign="center" my={2}>
                    No Tenants
                  </Box>
                ),
              }}
              pageSizeOptions={[5, 10]}
              checkboxSelection
              autoHeight
            />
          </Box>
        </>
      )}
      {addTenantModalOpen && (
        <AddTenantToAccessGroupModal
          open={addTenantModalOpen}
          targetGroup={targetGroup}
          existingTenants={groupTenants}
          handleCancel={() => setAddTenantModalOpen(false)}
          handleConfirm={handleAddTenants}
        />
      )}
    </Box>
  );
}
